import React, { useCallback, useEffect, useRef, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Loader2, X } from "lucide-react";

import { supabase } from "~/integrations/supabase/client";

interface ClientSelectorProps {
  value: string | undefined;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
}

const INITIAL_RESULTS = 20;
const RESULTS_PER_PAGE = 20;

const ClientSelector: React.FC<ClientSelectorProps> = ({
  value,
  onChange,
  className,
  disabled,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [displayCount, setDisplayCount] = useState(INITIAL_RESULTS);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Fetch clients for the dropdown
  const { data: clients, isLoading } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("id, name")
        .order("name");

      if (error) {
        console.error("Error fetching clients:", error);
        throw new Error("No se pudieron cargar los clientes");
      }

      return data || [];
    },
  });

  // Filter clients based on search term
  const filteredClients =
    clients?.filter((client) =>
      client.name.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

  // Get displayed clients (with pagination)
  const displayedClients = filteredClients.slice(0, displayCount);
  const hasMoreResults = filteredClients.length > displayCount;

  // Get selected client for display
  const selectedClient = clients?.find((client) => client.id === value);

  // Reset display count when search term changes
  useEffect(() => {
    setDisplayCount(INITIAL_RESULTS);
    setHighlightedIndex(-1);
  }, [searchTerm]);

  // Handle scroll to load more results
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;

      if (isNearBottom && hasMoreResults && !isLoadingMore) {
        setIsLoadingMore(true);

        // Simulate a small delay for better UX
        setTimeout(() => {
          setDisplayCount((prev) =>
            Math.min(prev + RESULTS_PER_PAGE, filteredClients.length)
          );
          setIsLoadingMore(false);
        }, 150);
      }
    },
    [hasMoreResults, isLoadingMore, filteredClients.length]
  );

  // Handle outside clicks to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled || isLoading) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          const nextIndex =
            highlightedIndex < displayedClients.length - 1
              ? highlightedIndex + 1
              : 0;
          setHighlightedIndex(nextIndex);

          // Auto-load more if navigating to the last few items
          if (
            nextIndex >= displayedClients.length - 3 &&
            hasMoreResults &&
            !isLoadingMore
          ) {
            setIsLoadingMore(true);
            setTimeout(() => {
              setDisplayCount((prev) =>
                Math.min(prev + RESULTS_PER_PAGE, filteredClients.length)
              );
              setIsLoadingMore(false);
            }, 150);
          }
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : displayedClients.length - 1
          );
        }
        break;
      case "Enter":
        e.preventDefault();
        if (
          isOpen &&
          highlightedIndex >= 0 &&
          displayedClients[highlightedIndex]
        ) {
          handleSelectClient(displayedClients[highlightedIndex]);
        } else if (!isOpen) {
          setIsOpen(true);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  const handleSelectClient = (client: { id: string; name: string }) => {
    onChange(client.id);
    setSearchTerm("");
    setIsOpen(false);
    setHighlightedIndex(-1);
    setDisplayCount(INITIAL_RESULTS);
  };

  const handleClear = () => {
    onChange("");
    setSearchTerm("");
    setIsOpen(false);
    setHighlightedIndex(-1);
    setDisplayCount(INITIAL_RESULTS);
    inputRef.current?.focus();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleInputFocus = () => {
    if (!disabled && !isLoading) {
      setIsOpen(true);
    }
  };

  const displayValue = selectedClient ? selectedClient.name : searchTerm;

  return (
    <div className={`relative ${className || ""}`} ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          disabled={disabled || isLoading}
          placeholder={isLoading ? "Cargando clientes..." : "Buscar cliente..."}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        />

        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          ) : selectedClient ? (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 text-gray-400 hover:text-gray-600"
              disabled={disabled}
            >
              <X className="h-3 w-3" />
            </button>
          ) : (
            <ChevronDown
              className={`h-4 w-4 text-gray-400 transition-transform ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          )}
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && !isLoading && (
        <div className="absolute left-0 top-full z-50 mt-1 w-full origin-top rounded-md border border-gray-200 bg-white shadow-lg duration-200 ease-out animate-in fade-in-0 zoom-in-95 slide-in-from-top-2">
          <div
            ref={scrollContainerRef}
            className="max-h-60 overflow-y-auto py-1"
            onScroll={handleScroll}
          >
            {displayedClients.length > 0 ? (
              <>
                {displayedClients.map((client, index) => (
                  <button
                    key={client.id}
                    type="button"
                    onClick={() => handleSelectClient(client)}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                      index === highlightedIndex ? "bg-gray-100" : ""
                    } ${client.id === value ? "bg-blue-50 text-blue-900" : ""}`}
                  >
                    {client.name}
                  </button>
                ))}

                {/* Load more indicator */}
                {hasMoreResults && (
                  <div className="border-t border-gray-100 px-4 py-2 text-center text-xs text-gray-500">
                    {isLoadingMore ? (
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Cargando más...
                      </div>
                    ) : (
                      <div className="text-gray-400">
                        Mostrando {displayedClients.length} de{" "}
                        {filteredClients.length} clientes
                        <br />
                        <span className="text-xs">Desplázate para ver más</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="px-4 py-2 text-sm text-gray-500">
                {searchTerm
                  ? "No se encontraron clientes"
                  : "No hay clientes disponibles"}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientSelector;
